import * as THREE from 'three';
import { Layer } from '../RenderLayers';

/* ═══════════════════════════════════════════════════════════════════════════
   Les TRAÎNÉES — des rubans (PLAN.md §4.7, livrable J5).

   La traînée de propulsion du vaisseau. L'écueil, découvert à l'usage : le monde
   DÉFILE alors que le vaisseau reste à une hauteur d'écran quasi fixe. Un ruban
   ancré en espace-vaisseau ne s'écoule donc pas — il reste collé sous la coque
   comme un bout de scotch. Un jet de réacteur, lui, est laissé DERRIÈRE dans le
   niveau : chaque bouffée reste dans le décor et défile vers le bas pendant que
   le vaisseau file vers le haut.

   La solution est celle de `SparkField` : on mémorise le DÉCALAGE DE DÉFILEMENT
   à l'émission de chaque point, et on décale son ordonnée de `scroll - scrollÀ
   L'émission`. La tête colle au vaisseau (décalage nul), la queue dérive vers le
   bas — une vraie traînée qui s'écoule, et qui se courbe quand le vaisseau
   louvoie latéralement.

   Trois précautions contre le « ruban invisible » ou moche :

   1. `side: DoubleSide` — dans une courbe, la moitié des triangles tourne le dos
      à la caméra ; en FrontSide ils disparaîtraient.
   2. On lâche un point à CADENCE DE DÉFILEMENT (tous les `STEP` unités de scroll),
      pas au gré du mouvement du vaisseau : le ruban s'écoule même à l'arrêt
      latéral, puisque le vaisseau avance toujours dans le niveau.
   3. Bande dans la scène principale (couche VFX) → bloomée comme une flamme, et
      sous la couche danger : une traînée ne masque jamais un projectile.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Points le long du ruban (tête → queue). */
const POINTS = 24;
/** Demi-largeur du ruban à la tête, en unités monde. S'affine jusqu'à 0. */
const HEAD_HALF_WIDTH = 8;
/** Défilement (en unités) entre deux points lâchés — l'espacement de la traînée. */
const STEP = 9;
/** Profondeur du ruban : DERRIÈRE le vaisseau (à z=0), devant le décor (≤ −180).
 *  C'est la profondeur, pas le renderOrder, qui le place — three dessine toute la
 *  liste opaque (coques) avant la transparente, donc un `renderOrder` sous le
 *  vaisseau NE SUFFIT PAS (même piège que le scrim, voir Backdrop.ts). Avec
 *  `depthTest`, la coque déjà dessinée rejette les fragments du ruban qui sont
 *  derrière elle : le jet émerge de SOUS le vaisseau au lieu de le recouvrir. */
const TRAIL_Z = -20;

const VERT = /* glsl */ `
attribute float aFade;
varying float vFade;
void main() {
  vFade = aFade;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */ `
precision highp float;
uniform vec3 uColor;
varying float vFade;
void main() {
  // Additif : la contribution ajoutée vaut couleur × opacité. Le surplus à la
  // tête (>1) nourrit le bloom et donne la braise du réacteur.
  gl_FragColor = vec4(uColor * (0.6 + vFade), vFade);
}
`;

export class TrailRibbons {
  readonly mesh: THREE.Mesh;
  private readonly posAttr: THREE.BufferAttribute;
  private readonly fadeAttr: THREE.BufferAttribute;
  private readonly geo: THREE.BufferGeometry;

  /** Anneau des points, tête en 0 : position monde + décalage de défilement à
   *  l'émission (pour la dérive). */
  private readonly hx = new Float32Array(POINTS);
  private readonly hy = new Float32Array(POINTS);
  private readonly hScroll = new Float32Array(POINTS);
  private filled = 0;
  private lastDropScroll = 0;
  private strength = 0;

  /** Positions RENDUES (après dérive), réutilisées à chaque reconstruction. */
  private readonly rx = new Float32Array(POINTS);
  private readonly ry = new Float32Array(POINTS);

  constructor() {
    const n = POINTS * 2;
    this.geo = new THREE.BufferGeometry();
    this.posAttr = new THREE.BufferAttribute(new Float32Array(n * 3), 3);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.fadeAttr = new THREE.BufferAttribute(new Float32Array(n), 1);
    this.fadeAttr.setUsage(THREE.DynamicDrawUsage);
    this.geo.setAttribute('position', this.posAttr);
    this.geo.setAttribute('aFade', this.fadeAttr);

    // Index fixe : deux triangles par segment entre points consécutifs.
    const idx: number[] = [];
    for (let i = 0; i < POINTS - 1; i++) {
      const a = i * 2;
      const b = i * 2 + 1;
      const c = (i + 1) * 2;
      const d = (i + 1) * 2 + 1;
      idx.push(a, b, c, b, d, c);
    }
    this.geo.setIndex(idx);
    this.geo.setDrawRange(0, 0);

    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: { uColor: { value: new THREE.Color(0x8fe8ff) } },
      transparent: true,
      depthWrite: false,
      // ⚠️ `depthTest: true` — c'est LUI qui range le ruban derrière la coque
      // (voir TRAIL_Z). En `false`, le ruban se dessinait par-dessus le vaisseau
      // (liste transparente après la liste opaque), d'où le « au-dessus du
      // vaisseau ». On ne réécrit pas la profondeur (`depthWrite: false`) : le
      // ruban n'a aucune raison d'occulter ce qui vient après lui.
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
    // Catalogue de matériaux fermé (§4.8) : clé explicite, comme tout shader injecté.
    mat.customProgramCacheKey = (): string => 'trail-ribbon-v1';

    this.mesh = new THREE.Mesh(this.geo, mat);
    this.mesh.frustumCulled = false;
    // JUSTE SOUS le vaisseau. Le ruban a `depthTest: false` (superposition par
    // renderOrder) : le poser au-dessus de `Layer.Player` le dessinerait
    // PAR-DESSUS la coque — un cache moche sur le vaisseau au lieu d'un jet
    // derrière lui. Sous le vaisseau, au-dessus du décor : la place d'un réacteur.
    // Reste dans la scène principale, donc bloomé comme une flamme.
    this.mesh.renderOrder = Layer.Player - 1;
  }

  /** Vide le ruban — à l'entrée en partie, pour ne pas hériter d'un trait de la
   *  partie précédente. */
  reset(): void {
    this.filled = 0;
    this.strength = 0;
    this.geo.setDrawRange(0, 0);
  }

  /**
   * Fait suivre la traînée au vaisseau. `scroll` est le décalage de défilement
   * courant (celui de `SparkField`) ; `strength` ∈ [0,1] module l'opacité —
   * typiquement une base plus un supplément selon la vitesse.
   */
  follow(x: number, y: number, scroll: number, strength: number): void {
    this.strength = strength < 0 ? 0 : strength > 1 ? 1 : strength;

    if (this.filled === 0) {
      this.hx[0] = x;
      this.hy[0] = y;
      this.hScroll[0] = scroll;
      this.filled = 1;
      this.lastDropScroll = scroll;
    } else {
      // La tête colle TOUJOURS au vaisseau, au décalage courant.
      this.hx[0] = x;
      this.hy[0] = y;
      this.hScroll[0] = scroll;
      // On lâche un point quand le décor a défilé d'un cran : la traînée s'écoule
      // à cadence constante, indépendamment du louvoiement du vaisseau.
      if (scroll - this.lastDropScroll >= STEP) {
        for (let i = Math.min(this.filled, POINTS - 1); i > 0; i--) {
          this.hx[i] = this.hx[i - 1]!;
          this.hy[i] = this.hy[i - 1]!;
          this.hScroll[i] = this.hScroll[i - 1]!;
        }
        this.hx[0] = x;
        this.hy[0] = y;
        this.hScroll[0] = scroll;
        if (this.filled < POINTS) this.filled++;
        this.lastDropScroll = scroll;
      }
    }

    this.rebuild(scroll);
  }

  private rebuild(scroll: number): void {
    const n = this.filled;
    if (n < 2 || this.strength <= 0.001) {
      this.geo.setDrawRange(0, 0);
      return;
    }

    // 1) Positions rendues : chaque point dérive vers le bas de ce que le décor a
    //    défilé depuis son émission (exactement comme les débris de SparkField).
    for (let i = 0; i < n; i++) {
      this.rx[i] = this.hx[i]!;
      this.ry[i] = this.hy[i]! - (scroll - this.hScroll[i]!);
    }

    const pos = this.posAttr.array as Float32Array;
    const fade = this.fadeAttr.array as Float32Array;

    // 2) Bande : perpendiculaire à la tangente du chemin RENDU, largeur et
    //    opacité décroissantes vers la queue.
    for (let i = 0; i < n; i++) {
      let tx: number;
      let ty: number;
      if (i === 0) {
        tx = this.rx[0]! - this.rx[1]!;
        ty = this.ry[0]! - this.ry[1]!;
      } else if (i === n - 1) {
        tx = this.rx[i - 1]! - this.rx[i]!;
        ty = this.ry[i - 1]! - this.ry[i]!;
      } else {
        tx = this.rx[i - 1]! - this.rx[i + 1]!;
        ty = this.ry[i - 1]! - this.ry[i + 1]!;
      }
      const len = Math.sqrt(tx * tx + ty * ty) || 1;
      const nx = -ty / len;
      const ny = tx / len;

      const t = i / (POINTS - 1);
      const w = HEAD_HALF_WIDTH * (1 - t);
      const a = this.strength * (1 - t) * (1 - t);

      const px = this.rx[i]!;
      const py = this.ry[i]!;
      const o = i * 6;
      pos[o] = px + nx * w;
      pos[o + 1] = py + ny * w;
      pos[o + 2] = TRAIL_Z;
      pos[o + 3] = px - nx * w;
      pos[o + 4] = py - ny * w;
      pos[o + 5] = TRAIL_Z;
      fade[i * 2] = a;
      fade[i * 2 + 1] = a;
    }

    this.posAttr.needsUpdate = true;
    this.fadeAttr.needsUpdate = true;
    this.geo.setDrawRange(0, (n - 1) * 6);
  }
}
